<template>
  <div
    v-if="dataHasAdditionalFields"
    class="additional-fields-accordion"
  >
    <accordion-panel
      :id="`additional-accordion-${id}`"
      :collapsed.sync="collapsedProxy"
    >
      <template #title>
        <span class="d-inline-flex align-items-center">
          {{ $t('reports.additional-fields') }}
        </span>
        <div class="d-inline-flex flex-grow-1">
          <hr class="additional-fields-accordion__horizontal-line"/>
        </div>
      </template>
      <div class="additional-fields-accordion__general-fields">
        <div
          v-for="(field, index) in generalFields"
          class="additional-fields-accordion__field-wrapper"
          :key="index"
        >
          <strong class="additional-fields-accordion__label">
            {{ field.text }}
          </strong>
          <span class="additional-fields-accordion__field">
            {{ field.value }}
          </span>
        </div>
      </div>
      <div class="additional-fields-accordion__free-fill-fields">
        <div
          v-for="(field, index) in freeFillFields"
          class="additional-fields-accordion__field-wrapper"
          :key="index"
        >
          <strong class="additional-fields-accordion__free-fill-label">
            {{ field.text }}
          </strong>
          <span class="additional-fields-accordion__field">
          {{ field.value }}
        </span>
        </div>
      </div>
    </accordion-panel>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import AccordionPanel from '@/components/AccordionPanel';
import allAdditionalFields from '@/domain/data/generalPanel/reports/additionalFields/allAdditionalFields';
import { isValid, formatter } from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';

const FREE_FILL_FIELDS = [
  'assetDescription',
  'manufacturer',
  'observation',
  'transformerMaintenanceHistory',
  'transformerObservation',
  'circuitBreakerMaintenanceHistory',
  'circuitBreakerObservation',
  'disconnectorMaintenanceHistory',
  'disconnectorObservation',
];

export default {
  name: 'AdditionalFieldsAccordion',
  components: { AccordionPanel },
  props: {
    expanded: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [String, Number],
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    fieldsToRemove: {
      type: Array,
      default: () => ([]),
    },
  },
  data: vm => ({
    additionalFields: allAdditionalFields(vm),
  }),
  computed: {
    collapsedProxy: {
      get() {
        return !this.expanded;
      },
      set(value) {
        this.$emit('update:expanded', !value);
      },
    },
    dataHasAdditionalFields() {
      return !isEmpty(this.additionalFieldsFromData);
    },
    additionalFieldsFromData() {
      const additionalFieldsValues = this.additionalFields.map(field => field.value);
      const dataFields = Object.entries(this.data);
      const additionalFieldsFromData = dataFields.filter(
        item => item[1] !== undefined && additionalFieldsValues.includes(item[0]),
      );
      return Object.fromEntries(additionalFieldsFromData);
    },
    mappedAdditionalFields() {
      const additionalFieldsObjectsFromData = this.additionalFields.filter(
        item => Object.keys(this.additionalFieldsFromData).includes(item.value),
      );
      let mappedAdditionalFields = additionalFieldsObjectsFromData.map(item => ({
        ...item,
        property: item.value,
        value: this.getFieldValue(item),
      }));
      mappedAdditionalFields = this.removeFields(mappedAdditionalFields);
      return mappedAdditionalFields;
    },
    generalFields() {
      return this.mappedAdditionalFields.filter(
        field => !FREE_FILL_FIELDS.includes(field.property),
      );
    },
    freeFillFields() {
      return this.mappedAdditionalFields.filter(
        field => FREE_FILL_FIELDS.includes(field.property),
      );
    },
  },
  methods: {
    removeFields(array) {
      if (!this.fieldsToRemove.length) {
        return array;
      }
      return array.filter(item => !this.fieldsToRemove.includes(item.property));
    },
    getFieldValue(item) {
      const itemValue = this.additionalFieldsFromData[item.value];
      if (itemValue === undefined || itemValue === null) return '-';
      const types = {
        string: this.getStringValue,
        number: this.formatNumberValue,
        boolean: this.getBooleanValue,
      };
      let itemValueTypeFunction = types[typeof itemValue];
      if (isValid(itemValue)) {
        itemValueTypeFunction = this.formatDateValue;
      }
      return itemValueTypeFunction ? itemValueTypeFunction(itemValue, item) : itemValue;
    },
    getBooleanValue(value) {
      return value ? this.$t('general.yes') : this.$t('general.no');
    },
    getStringValue(value, item) {
      if (item.enumLabelPrefix) {
        return this.$t(`${item.enumLabelPrefix}${value}`);
      }
      return value;
    },
    formatNumberValue(value, item) {
      if (item.precision) {
        return this.$n(value, {
          minimumFractionDigits: item.precision,
          maximumFractionDigits: item.precision,
        });
      }
      return Number(value);
    },
    formatDateValue(value) {
      return formatter(value, formats.DATE_SHORT, timezones.UTC, '-');
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"
  @import "src/styles/defaults/variables"

  .additional-fields-accordion
    &__free-fill-fields
      .additional-fields-accordion
        &__label, &__field
          margin-bottom: 15px
    &__free-fill-label
      width: 18vw
      text-align: right
      justify-content: flex-end
    &__horizontal-line
      width: 100%
      margin-left: 10px
      border-top: 1px solid $color-gray-medium-highlight
    &__label
      text-align: right
      justify-content: flex-end
      min-width: 18vw
      max-width: 18vw
    &__field
      width: unset
      margin-left: 15px
    &__field, &__label, &__free-fill-label
      display: inline-flex
      white-space: normal
      word-break: break-word
    &__field-wrapper
      white-space: nowrap
      display: flex
    &__general-fields
      display: grid
      grid-gap: 10px
      grid-template-columns: repeat(3, 1fr)
      margin: 0 0 15px 5px
      +media-max-sm
        grid-template-columns: auto
      +media-md
        margin-left: 0
        grid-template-columns: repeat(2, auto)
    ::v-deep
      .accordion-panel
        box-shadow: none
        &__slot-container
          margin-left: 0 !important
        &__right-controls
          width: 30px
          i
            font-size: 1.3rem
        &__title-container
          color: $color-primary
          font-weight: bold
          display: flex
          white-space: nowrap
          max-width: calc(100% - 30px)
        &__header
          padding: 0 !important
          background-color: transparent
</style>
